import { action, computed, observable } from 'mobx'
import { type IValidationSchema, validationContext } from 'validx'
import type { LegendPartDependencies } from '@/features/legends/editor/legend-part-dependencies'
import { LegendPartViewModelBase } from '@/features/legends/editor/LegendPartViewModelBase'
import {
  type ErrorDescriptor,
  receiveErrors,
} from '@/features/legends/editor/analyzer-errors'
import { validateObject } from '@/utils/validx-util'
import type { Effect } from './effects'
import { MutatorsBuilderViewModel } from './MutatorsBuilderViewModel'
import { type MutatorTypeInfo, mutatorTypes } from './mutator-registry'

/**
 * The question an effect belongs to. A select option's effect belongs to
 * its question too.
 */
export interface EffectContext {
  readonly type: string
  readonly id: string
}

export interface EffectEditorViewModelOptions {
  dependencies: LegendPartDependencies
  context: EffectContext
}

const allMutatorTypes = Object.values(mutatorTypes)
const serviceItemMutatorTypes = [mutatorTypes.InventoryItem]

/**
 * What happens when a question is answered a certain way: a list of mutators.
 */
export class EffectEditorViewModel extends LegendPartViewModelBase {
  private readonly _schema: IValidationSchema<EffectEditorViewModel> = {
    mutators: [validateObject('There are invalid mutators.')],
  }

  /**
   * Keeps the effect's accordion open across renders.
   */
  @observable accessor isExpanded = false

  readonly context: EffectContext
  readonly mutators: MutatorsBuilderViewModel
  readonly validation = validationContext(this, this._schema)

  constructor({ dependencies, context }: EffectEditorViewModelOptions) {
    super(dependencies)
    this.context = context
    this.mutators = new MutatorsBuilderViewModel({ effect: this, dependencies })
    this.lifecycle.manage(this.mutators)
  }

  /**
   * Whether the effect belongs to a numeric question.
   */
  @computed
  get isNumericQuestion() {
    return this.context.type === 'Numeric'
  }

  /**
   * Whether the effect belongs to a select question.
   */
  @computed
  get isSelectQuestion() {
    return this.context.type === 'Select'
  }

  /**
   * The symbols the effect's formulas can use.
   */
  @computed
  get symbolTable() {
    return this.dependencies.symbols.getSymbolTableFor(this.context)
  }

  @computed
  private get _json(): Effect {
    return { mutators: this.mutators.toJSON() }
  }

  /**
   * The mutators that can be added. Numeric questions and a select
   * question's own effect only change service items.
   *
   * @param descriptorId The ID of the effect descriptor the effect is shown
   * under; a select question's own effect has the question's ID.
   */
  getAvailableMutatorTypes(descriptorId: string): MutatorTypeInfo[] {
    return this.isNumericQuestion || this._isSelectQuestionRoot(descriptorId)
      ? serviceItemMutatorTypes
      : allMutatorTypes
  }

  /**
   * Whether service item mutators set an amount. Numeric questions and a
   * select question's own effect use the answer instead.
   *
   * @param descriptorId See {@link getAvailableMutatorTypes}.
   */
  hasServiceItemAmount(descriptorId: string) {
    return !this.isNumericQuestion && !this._isSelectQuestionRoot(descriptorId)
  }

  /**
   * Hydrates the mutators and returns the rest of the data, since effects
   * share their object with the question or option that owns them.
   */
  @action
  hydrate<T extends Effect>(
    effect: T | undefined,
  ): Omit<T, 'mutators'> | undefined {
    this.mutators.hydrate(effect?.mutators)
    if (!effect) {
      return undefined
    }
    const { mutators: _mutators, ...rest } = effect
    return rest
  }

  /**
   * Opens or closes the effect's accordion.
   */
  @action.bound
  setExpanded(isExpanded: boolean) {
    this.isExpanded = isExpanded
  }

  /**
   * Shows the analyzer's errors on the mutators.
   */
  receiveErrors(descriptor: ErrorDescriptor) {
    receiveErrors(this, descriptor.inner)
  }

  /**
   * The effect as the legend document has it.
   */
  toJSON(): Effect {
    return this._json
  }

  private _isSelectQuestionRoot(descriptorId: string) {
    return this.isSelectQuestion && descriptorId === this.context.id
  }
}
